import { ReactElement, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import { useAuth } from '../contextProviders/AuthProvider';
import useEmailTemplates from '../dataHooks/useEmailTemplates';
import { useEmailTemplateMutators } from '../dataMutators/useEmailTemplateMutators';
import EmailTemplate from '../types/EmailTemplate';

function EmailTemplateEditor({ template }: { template: EmailTemplate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [subjectTemplate, setSubjectTemplate] = useState(template.subjectTemplate);
  const [htmlTemplate, setHtmlTemplate] = useState(template.htmlTemplate);
  const [textTemplate, setTextTemplate] = useState(template.textTemplate);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const { updateTemplate } = useEmailTemplateMutators();

  const hasChanges =
    subjectTemplate !== template.subjectTemplate ||
    htmlTemplate !== template.htmlTemplate ||
    textTemplate !== template.textTemplate;

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await updateTemplate(template.key, {
        subjectTemplate,
        htmlTemplate,
        textTemplate,
      });
      setSaveMessage({ text: 'Saved', type: 'success' });
    } catch {
      setSaveMessage({ text: 'Failed to save', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSubjectTemplate(template.subjectTemplate);
    setHtmlTemplate(template.htmlTemplate);
    setTextTemplate(template.textTemplate);
    setSaveMessage(null);
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack>
          <Typography variant="titleMedium">{template.name}</Typography>
          <Typography variant="bodySmall" sx={{
            color: "text.secondary"
          }}>
            {template.key}
          </Typography>
        </Stack>
        <Typography variant="bodyMedium" sx={{
          color: "text.secondary"
        }}>
          {isExpanded ? 'Collapse' : 'Expand'}
        </Typography>
      </Box>
      <Collapse in={isExpanded}>
        <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
          {/* Available variables */}
          <Box>
            <Typography
              variant="bodySmall"
              sx={{
                color: "text.secondary",
                mb: 0.5
              }}>
              Available variables:
            </Typography>
            <Stack
              direction="row"
              sx={{
                flexWrap: "wrap",
                gap: 0.5
              }}>
              {template.variables.map((v) => (
                <Chip key={v} label={`\${${v}}`} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>

          {/* Subject */}
          <TextField
            label="Subject Template"
            value={subjectTemplate}
            onChange={(e) => {
              setSubjectTemplate(e.target.value);
              setSaveMessage(null);
            }}
            fullWidth
            size="small"
          />

          {/* HTML Template */}
          {isPreview ? (
            <Box>
              <Typography
                variant="bodySmall"
                sx={{
                  color: "text.secondary",
                  mb: 0.5
                }}>
                HTML Preview (raw — variables not resolved):
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  maxHeight: '25rem',
                  overflow: 'auto',
                  bgcolor: 'background.paper',
                }}
                dangerouslySetInnerHTML={{ __html: htmlTemplate }}
              />
            </Box>
          ) : (
            <TextField
              label="HTML Template"
              value={htmlTemplate}
              onChange={(e) => {
                setHtmlTemplate(e.target.value);
                setSaveMessage(null);
              }}
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              size="small"
              slotProps={{
                input: {
                  sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                },
              }}
            />
          )}

          {/* Text Template */}
          <TextField
            label="Text Template"
            value={textTemplate}
            onChange={(e) => {
              setTextTemplate(e.target.value);
              setSaveMessage(null);
            }}
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            size="small"
            slotProps={{
              input: {
                sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
              },
            }}
          />

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              size="small"
            >
              {isSaving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={!hasChanges}
              size="small"
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsPreview(!isPreview)}
              size="small"
            >
              {isPreview ? 'Edit HTML' : 'Preview HTML'}
            </Button>
            {saveMessage && (
              <Typography
                variant="bodySmall"
                color={saveMessage.type === 'success' ? 'success.main' : 'error.main'}
              >
                {saveMessage.text}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function EmailTemplatesPage(): ReactElement {
  const { isGod } = useAuth();
  const { templates, isTemplatesLoading, templatesError } = useEmailTemplates();
  const { seedDefaults } = useEmailTemplateMutators();
  const [isSeeding, setIsSeeding] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [seedMessage, setSeedMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const handleSeedDefaults = async () => {
    setIsSeeding(true);
    setSeedMessage(null);
    try {
      await seedDefaults();
      setSeedMessage({ text: 'Default templates seeded successfully', type: 'success' });
      setConfirmingReset(false);
    } catch {
      setSeedMessage({ text: 'Failed to seed defaults', type: 'error' });
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <GratiPageWrapper
      isAuthRequired={true}
      isNoAuthRequired={false}
      validateUser={() => isGod}
      isContentLoading={isTemplatesLoading}
      contentError={templatesError}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="headlineLarge" sx={{ mb: 2 }}>
          Email Templates
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mb: 3
          }}>
          {!confirmingReset ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setConfirmingReset(true)}
              disabled={isSeeding}
              size="small"
            >
              Seed / Reset Defaults
            </Button>
          ) : (
            <>
              <Typography variant="bodyMedium" sx={{
                color: "warning.main"
              }}>
                This will overwrite all templates with defaults.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={handleSeedDefaults}
                disabled={isSeeding}
                size="small"
              >
                {isSeeding ? <CircularProgress size={20} /> : 'Confirm Reset'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setConfirmingReset(false)}
                size="small"
              >
                Cancel
              </Button>
            </>
          )}
          {seedMessage && (
            <Typography
              variant="bodySmall"
              color={seedMessage.type === 'success' ? 'success.main' : 'error.main'}
            >
              {seedMessage.text}
            </Typography>
          )}
        </Stack>

        {templates && templates.length === 0 && (
          <Typography
            variant="bodyMedium"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            No templates found. Click &quot;Seed / Reset Defaults&quot; to create the default templates.
          </Typography>
        )}

        <Stack spacing={2}>
          {templates?.map((template) => (
            <EmailTemplateEditor key={template.key} template={template} />
          ))}
        </Stack>
      </Box>
    </GratiPageWrapper>
  );
}
